/**
 * Studio (iterate editor) — Phase 4: approve, test render, persistence,
 * channel promotion.
 *
 * Phase 1 mounted the REAL pipeline composition (src/remotion/
 * ClipComposition.tsx) in @remotion/player fed by GET
 * /api/clips/:id/studio-inputs. Phase 2 layered the ClipEditState
 * override system on top. Phase 3 added the chat loop. Phase 4 makes the
 * edits REAL:
 *
 *   inputProps ── applyEditState(journal + livePatch) ──▶ Player
 *                        │
 *                        ├─ autosave ──▶ PUT /api/clips/:id/edit-state
 *                        ├─ [Test render] ──▶ ~6s slice through the REAL
 *                        │                    pipeline renderer (modal)
 *                        ├─ [Approve & render ✓] ──▶ bake cue edits into
 *                        │        arabic.srt + render-only re-render with
 *                        │        the design applied (Library updates,
 *                        │        old version restorable from history)
 *                        └─ Save design to channel ──▶ promote the
 *                                 accumulated layout/brand into the
 *                                 channel template (future clips render
 *                                 with it by construction)
 *
 * PERSISTENCE MODEL (documented UX decision): the SERVER file
 * work/<clip>/edit-state.json is the source of truth. On open, the server
 * state wins UNLESS the localStorage draft strictly EXTENDS it (same
 * entries plus more) — then the draft is the unsaved buffer ahead of the
 * server and autosave pushes it up. This one rule covers first-load
 * migration (empty server = trivial prefix), crash recovery, and staleness
 * (a diverged draft loses). localStorage stays only as that crash buffer;
 * every journal change autosaves to the server after ~800ms. Autosave
 * PAUSES while an approve-render is in flight so the render consumes
 * exactly the approved journal.
 */
import { AlertTriangle, ArrowLeft, Check, FlaskConical, Loader2, PanelLeftClose, Pause, Play, RotateCcw, Sparkles, Undo2, X } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Player, type PlayerRef } from "@remotion/player";
import { ClipComposition } from "../../../src/remotion/ClipComposition.tsx";
import {
  appendEntry,
  applyEditState,
  baselineBrand,
  baselineLayout,
  emptyEditState,
  hasPendingChannelEntries,
  journalExtends,
  touchesLayoutOrBrand,
  undoLast,
  type ClipEditState,
  type EditJournalEntry,
  type EditPatch,
  type EditSource,
} from "../../../src/lib/clipEditState.ts";
import { browserMeasureFor, fitEntryForDesignCommit } from "../lib/measuredFitCommit.ts";
import {
  adoptChannelFont,
  approveRender,
  deleteStylePreset,
  getEditState,
  getJob,
  getStudioInputs,
  getTestRenderStatus,
  listStylePresets,
  promoteDesign,
  putEditState,
  saveStylePreset,
  startTestRender,
  testRenderFileUrl,
  uploadChannelFont,
  type CustomStylePreset,
  type StudioInputs,
  type TestRenderWindow,
} from "../api";
import { EditClipBar, EditClipDrawer, EDIT_GROUP_DONE, editLocked, type EditGroupId } from "../components/editClip/EditClipBar";
import { displayTitle, isTerminalStatus, toView, type JobView } from "../components/editClip/jobView";
import { StudioModal } from "../components/editClip/StudioModal";
import { FocusEditor } from "../components/focus/FocusEditor";
import { FocusPanel, type FocusPanelProps } from "../components/focus/FocusPanel";
import { StudioCanvasOverlay } from "../components/StudioCanvasOverlay";
import { StudioChatDock, type DockPanelProps } from "../components/StudioChatDock";
import { StudioHistoryPanel } from "../components/StudioHistoryPanel";
import type { FontAddResult, StudioInspectorProps } from "../components/StudioInspector";
import type { PreviewSeek } from "../components/TrimExtendPanel";
import { ctaGhostStyle, ctaSmallStyle, iconBtnStyle } from "../components/styles";
import {
  loadDockCollapsed,
  loadDockMode,
  saveDockCollapsed,
  saveDockMode,
  type ChatDockMode,
} from "../lib/chatThreads";
import { clearDraft, loadDraft, saveDraft } from "../lib/studioDraft";
import { NO_SELECTION, type StudioSelection } from "../lib/studioTypes";
import { useIsMobile } from "../lib/useIsMobile";
import { useTour } from "../components/tour/tourContext";
import { TOUR_SHEET_PAD } from "../lib/tour/placement";

/** Breathing room under the stage before the cue timeline — also the term
 *  that keeps the stage from being the reason the page scrolls. */
const STAGE_BOTTOM_GAP = 24;
/**
 * The cue-timeline strip's own height (its label + one row of cue blocks +
 * the scrollbar gutter), BUDGETED into the stage.
 *
 * The stage used to take every pixel under the header, which pushed the
 * timeline past the fold and made the page scroll 272 px — so reaching the
 * timeline scrolled the preview away, and §3's "a stage that never scrolls
 * the page" was false on the landing screen (QA 2026-09-16, M4). Reserving
 * the strip costs the preview ~80 px and puts the two things the operator
 * uses together in the same viewport. The History panel may stay below the
 * fold: it is a log, not a control.
 */
const TIMELINE_ROW_H = 80;

/**
 * Below this viewport HEIGHT, the timeline strip is not budgeted into the
 * stage at all — it falls below the fold anyway (short viewport = the
 * preview's own share of `innerHeight` is already under ~400 px before the
 * ~200 px of chrome + the 80 px timeline row are subtracted), so paying the
 * `TIMELINE_ROW_H` tax there only shrinks the preview for a strip the
 * operator can't see without scrolling regardless. The preview wins on short
 * viewports (1280×800 measured ~313 px wide collapsed); the timeline keeps
 * its budgeted slice once there is room for both (1440×900 and taller).
 */
const TIMELINE_BUDGET_MIN_H = 860;

/**
 * Where the Studio LANDS: the Captions tab, no cue chosen.
 *
 * `NO_SELECTION` put the panel on its "click anything on the video" empty
 * state, so the transcript list §3 promises for exactly this state — search
 * box + every cue as a clickable line — was never on screen, on the one
 * screen whose job is "fix one wrong caption in under five seconds" (QA
 * 2026-09-16, M5). Captions are what a clip's Studio is opened for; the empty
 * state is still there for a real deselect (click the bare canvas).
 */
const CAPTION_NO_CUE: StudioSelection = { el: "caption", cueIdx: null };

interface Props {
  jobId: string;
  onBack: () => void;
  /** Navigate to Settings — the chat's balance chip is the one place the
   *  operator sees whose key is paying, so it has to be clickable. */
  onOpenSettings?: () => void;
}

/** Strip ALL `<em>` markers (incl. colored `<em c="#hex">` opens) + newlines
 *  for the one-line cue chip label — a colored span must never leak raw
 *  markup into the timeline (tour-caught). Pattern mirrors srt.ts EM_OPEN_RE. */
function cueLabel(text: string): string {
  return text
    .replace(/<em(?:\s+c="#[0-9a-fA-F]{6}")?>/g, "")
    .replace(/<\/em>/g, "")
    .replace(/\s*\n\s*/g, " · ")
    .trim();
}

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec - m * 60;
  return `${m}:${s.toFixed(1).padStart(4, "0")}`;
}

/** Draft TAGGED with the clip it belongs to — the tag is what makes the
 *  persistence StrictMode- and clip-swap-safe (see below). */
interface ClipDraft {
  clip: string;
  state: ClipEditState;
}

const hydrateDraft = (clip: string): ClipDraft => ({
  clip,
  state: loadDraft(clip) ?? emptyEditState(),
});

/** Server-sync lifecycle of the journal. */
type SyncStatus = "loading" | "saved" | "saving" | "unsaved" | "error";

interface TestRenderUi {
  id: string | null;
  status: "starting" | "running" | "done" | "failed";
  error?: string;
  window?: TestRenderWindow;
}

interface ApproveUi {
  phase: "confirm" | "rendering" | "failed";
  error?: string;
  /** What the done notice says when the followed job lands. Absent ⇒ the
   *  Approve & render wording. Edit-bar actions pass their own. */
  doneText?: string;
  /** Which Edit-bar group started the job. Absent ⇒ Approve & render. A
   *  bar failure must not offer Approve's "Try again" (that bakes + renders
   *  the design, not the action that failed) — round 1, item 4. */
  barGroup?: EditGroupId;
}

export function StudioEditor({ jobId, onBack, onOpenSettings }: Props): React.JSX.Element {
  const isMobile = useIsMobile();
  // Three columns need real width; below this the chat drops under the
  // player instead of squeezing all three.
  const [inputs, setInputs] = useState<StudioInputs | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [frame, setFrame] = useState(0);
  const [playing, setPlaying] = useState(false);
  const playerRef = useRef<PlayerRef>(null);

  // ---- Edit state: the journal + the in-flight (uncommitted) patch ----
  // Hydrated SYNCHRONOUSLY from the localStorage slot in the initializer
  // (a pure read — StrictMode's double-invoke is safe). The load effect
  // then reconciles against the SERVER state (see persistence model above).
  const [draft, setDraft] = useState<ClipDraft>(() => hydrateDraft(jobId));
  const editState = draft.clip === jobId ? draft.state : emptyEditState();
  const [livePatch, setLivePatch] = useState<EditPatch | null>(null);
  const [selection, setSelection] = useState<StudioSelection>(CAPTION_NO_CUE);
  const [resetArmed, setResetArmed] = useState(false);

  // ---- Server persistence ----
  const [sync, setSync] = useState<SyncStatus>("loading");
  /** JSON of the journal the server last acknowledged — autosave's dirty check. */
  const lastSavedRef = useRef<string | null>(null);
  /** Autosave stays off until the initial server reconcile finished. */
  const serverReadyRef = useRef(false);

  // ---- Phase 4 flows ----
  const [testUi, setTestUi] = useState<TestRenderUi | null>(null);
  const [approveUi, setApproveUi] = useState<ApproveUi | null>(null);
  const [doneNotice, setDoneNotice] = useState<string | null>(null);
  const [promoting, setPromoting] = useState(false);
  const [promoteNotice, setPromoteNotice] = useState<string | null>(null);
  // Measured fit (spec 2026-09-17): the sentence for a journal change that
  // split captions (see the live gate effect below the journal mutations).
  const [fitNotice, setFitNotice] = useState<string | null>(null);

  // ---- Edit this clip (spec 2026-09-16 §2.4): the bar's groups read their
  // props off a job view fetched once, and again after every followed job
  // (`jobEpoch` — which also remounts the groups with fresh state).
  const [jobView, setJobView] = useState<JobView | null>(null);
  const [jobEpoch, setJobEpoch] = useState(0);
  const [editBarOpen, setEditBarOpen] = useState(false);
  const [editGroup, setEditGroup] = useState<EditGroupId | null>(null);
  const editBarRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    let live = true;
    getJob(jobId)
      .then((j) => { if (live) setJobView(toView(j)); })
      .catch(() => { /* the bar says "loading" until a later refresh lands */ });
    return () => { live = false; };
  }, [jobId, jobEpoch]);
  // While the server is running a job for this clip (started here or
  // elsewhere), keep the view fresh so the bar's lock follows the truth;
  // stop the moment it is terminal (round 1, item 6).
  const jobViewStatus = jobView?.status;
  useEffect(() => {
    if (jobViewStatus === undefined || isTerminalStatus(jobViewStatus)) return;
    const iv = window.setInterval(() => {
      getJob(jobId).then((j) => setJobView(toView(j))).catch(() => { /* transient */ });
    }, 3000);
    return () => window.clearInterval(iv);
  }, [jobId, jobViewStatus]);
  const editLockedRef = useRef(false);
  /** The chat dock's "clip tools" → expand the bar on Captions & text and
   *  bring it into view. Locked while a build is in flight: the bar opens,
   *  the group stays closed. */
  const openClipTools = useCallback((): void => {
    setEditBarOpen(true);
    if (!editLockedRef.current) setEditGroup("captions");
    window.requestAnimationFrame(() => {
      editBarRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }, []);
  // Iterate chat is a togglable drawer (phase 3.1) — collapsed by default so
  // the preview + inspector own the full width; open state persists.
  const [chatOpen, setChatOpen] = useState<boolean>(() => {
    try { return localStorage.getItem("ql-studio-chat-open") === "1"; } catch { return false; }
  });
  const toggleChat = useCallback((next?: boolean) => {
    setChatOpen((o) => {
      const v = next ?? !o;
      try { localStorage.setItem("ql-studio-chat-open", v ? "1" : "0"); } catch { /* best effort */ }
      return v;
    });
  }, []);
  // The tour's "Or just say it" step points at the chat — which is hidden
  // whenever the drawer is closed (phones always; desktop in drawer mode) or
  // the docked column is folded to its rail (the ≤1400 px auto-collapse):
  // the anchor is then unmounted or 0×0, and the spotlight lands on nothing.
  // So the tour HOLDS the chat open for that one step: derived, never
  // persisted — the stored choice is untouched and the chat folds back the
  // moment the step moves on (operator, 2026-09-17).
  const tour = useTour();
  const tourHoldsChat = tour.isActive && tour.step?.anchor === "studio-chat";
  const chatVisible = chatOpen || tourHoldsChat;

  // ---- Chat dock (2026-09-15): left column (Palmier) or the floating
  // drawer the Studio shipped with. Mode + collapse persist; mobile is
  // always the drawer (a 360 px column plus a canvas doesn't fit a phone).
  const [dockMode, setDockMode] = useState<ChatDockMode>(() =>
    loadDockMode(typeof window === "undefined" ? 0 : window.innerWidth),
  );
  // Auto-collapse is a VIEWPORT threshold: `DOCK_EXPANDED_MIN_WIDTH` (1400)
  // minus the app chrome the Studio never gets (248 px sidebar + 2×40 px
  // `<main>` padding) = 1072 px of content — spec §4's "the 1280 px laptop
  // otherwise leaves a 256 px preview". So 1280 opens as a rail and 1440
  // stays expanded, which is right: at 1440 the preview is HEIGHT-limited
  // (399 px either way), so collapsing the chat there would hide the design
  // agent for zero pixels of video.
  //
  // Below the threshold the stored choice does NOT win — `loadDockCollapsed`
  // ignores it there, and `saveDockCollapsed` refuses to write one, so a
  // narrow visit can neither inherit nor create the 266 px preview QA found
  // at 1280 (B2). Opening the rail by hand still works for the visit.
  const [chatCollapsed, setChatCollapsed] = useState<boolean>(() =>
    loadDockCollapsed(typeof window === "undefined" ? 0 : window.innerWidth),
  );
  /** An assistant reply landed while the dock was a rail. */
  const [chatUnread, setChatUnread] = useState(false);
  // Read inside the (stable) turn callback — a ref keeps the dock from
  // re-rendering the whole chat every time the rail opens or closes.
  const collapsedRef = useRef(chatCollapsed);
  useEffect(() => { collapsedRef.current = chatCollapsed; }, [chatCollapsed]);
  /** The docked column drawn as its 48 px rail — the stored collapse, unless
   *  the tour is holding the chat open for its step (see `tourHoldsChat`). */
  const chatRail = chatCollapsed && !tourHoldsChat;

  const setCollapsed = useCallback((next: boolean) => {
    setChatCollapsed(next);
    saveDockCollapsed(next);
    if (!next) setChatUnread(false); // opening it IS reading it
  }, []);

  /** Switching mode must never make the chat disappear: docking closes the
   *  drawer (the column takes over), floating opens it. */
  const changeDockMode = useCallback((next: ChatDockMode) => {
    setDockMode(next);
    saveDockMode(next);
    if (next === "left") {
      toggleChat(false);
      setCollapsed(false);
    } else {
      toggleChat(true);
    }
  }, [toggleChat, setCollapsed]);

  // ---- Stage height: the Focus editor gets exactly the viewport that is
  // left under the Studio chrome, and sizes the preview to fit it. That is
  // what stops the page from scrolling to see the video (spec §3/§4). The
  // chrome above the stage is measured rather than guessed — it is the header
  // plus whichever notices are currently showing, which changes at runtime.
  //
  // Measured in DOCUMENT space (`rect.bottom + scrollY`), never viewport
  // space: a viewport-relative read would shrink the stage every time the
  // operator scrolls down to the History panel.
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [stageH, setStageH] = useState<number>(() => {
    if (typeof window === "undefined") return 560;
    const budget = window.innerHeight < TIMELINE_BUDGET_MIN_H ? 0 : TIMELINE_ROW_H;
    return Math.max(360, window.innerHeight - 200 - budget);
  });
  // `useLayoutEffect`, not `useEffect`: the seed below is a guess, and a
  // guess painted for one frame is a visible snap of the whole stage.
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const measure = (): void => {
      const rect = el.getBoundingClientRect();
      const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
      const chromeBottom = rect.bottom + scrollY;
      const budget = window.innerHeight < TIMELINE_BUDGET_MIN_H ? 0 : TIMELINE_ROW_H;
      setStageH(
        Math.max(360, Math.round(window.innerHeight - chromeBottom - STAGE_BOTTOM_GAP - budget)),
      );
    };
    measure();
    window.addEventListener("resize", measure);
    // The chrome grows and shrinks on its own (a render-done notice appears,
    // the header wraps at a narrow width) — re-measure when it does.
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    ro?.observe(el);
    return () => {
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, [inputs]);

  useEffect(() => {
    let cancelled = false;
    setInputs(null);
    setError(null);
    setSelection(CAPTION_NO_CUE);
    setLivePatch(null);
    setSync("loading");
    setTestUi(null);
    setApproveUi(null);
    setDoneNotice(null);
    setPromoteNotice(null);
    setEditBarOpen(false);
    setEditGroup(null);
    serverReadyRef.current = false;
    lastSavedRef.current = null;
    // Clip changed under a mounted Studio → swap to that clip's draft.
    setDraft((d) => (d.clip === jobId ? d : hydrateDraft(jobId)));
    Promise.all([
      getStudioInputs(jobId),
      getEditState(jobId).catch(() => null), // non-fatal — reconcile handles it
    ])
      .then(([inp, es]) => {
        if (cancelled) return;
        setInputs(inp);
        const local = loadDraft(jobId) ?? emptyEditState();
        if (es === null) {
          // Server state unreadable: edit on the local draft; autosave will
          // retry and either heal (server accepts) or keep flagging.
          setDraft({ clip: jobId, state: local });
          setSync("error");
        } else {
          const server = es.editState;
          const localWins =
            journalExtends(server, local) && local.journal.length > server.journal.length;
          setDraft({ clip: jobId, state: localWins ? local : server });
          lastSavedRef.current = JSON.stringify(server);
          setSync(localWins ? "unsaved" : "saved");
        }
        serverReadyRef.current = true;
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => { cancelled = true; };
  }, [jobId]);

  // Crash buffer: persist the journal to localStorage on every change
  // (empty journal clears the slot). Tag check: never save a draft that
  // belongs to a different clip.
  useEffect(() => {
    if (draft.clip === jobId) saveDraft(jobId, draft.state);
  }, [jobId, draft]);

  // AUTOSAVE → PUT /edit-state, debounced. Paused during approve-render so
  // the spawned render reads exactly the approved journal from disk.
  const approvePhase = approveUi?.phase ?? null;
  useEffect(() => {
    if (!serverReadyRef.current || draft.clip !== jobId) return;
    if (approvePhase === "rendering") return;
    const json = JSON.stringify(draft.state);
    if (json === lastSavedRef.current) return;
    setSync("unsaved");
    let stale = false;
    const t = window.setTimeout(() => {
      setSync("saving");
      putEditState(jobId, draft.state)
        .then(() => {
          if (stale) return;
          lastSavedRef.current = json;
          setSync("saved");
        })
        .catch(() => { if (!stale) setSync("error"); });
    }, 800);
    return () => { stale = true; window.clearTimeout(t); };
  }, [jobId, draft, approvePhase]);

  // Track playhead + play state off the Player's own events so the cue strip
  // and the time readout stay honest however playback was triggered.
  useEffect(() => {
    if (!inputs) return;
    const p = playerRef.current;
    if (!p) return;
    const onFrame: (e: { detail: { frame: number } }) => void = (e) => setFrame(e.detail.frame);
    const onPlay = (): void => setPlaying(true);
    const onPause = (): void => setPlaying(false);
    const onEnded = (): void => setPlaying(false);
    p.addEventListener("frameupdate", onFrame);
    p.addEventListener("play", onPlay);
    p.addEventListener("pause", onPause);
    p.addEventListener("ended", onEnded);
    return () => {
      p.removeEventListener("frameupdate", onFrame);
      p.removeEventListener("play", onPlay);
      p.removeEventListener("pause", onPause);
      p.removeEventListener("ended", onEnded);
    };
  }, [inputs]);

  // ---- Journal mutations (the ONLY paths that write edit state) ----
  // `scope` is set only by chat patches the operator confirmed as
  // channel-wide: the entry is tagged "channel-pending" until promotion
  // lifts it into the channel template.
  const commit = useCallback(
    (patch: EditPatch, label: string, source: EditSource, scope?: EditJournalEntry["scope"]): void => {
      setDraft((d) => ({
        clip: d.clip,
        state: appendEntry(d.state, { ts: Date.now(), source, label, patch, ...(scope ? { scope } : {}) }),
      }));
      setLivePatch(null);
    },
    [],
  );

  // ---- Measured fit, LIVE (spec 2026-09-17 §3.4) ----
  // The gate is a DERIVED check on the effective design, not a hook on one
  // mutation path: it runs after every journal change — load, commit, Undo,
  // "Undo to here", Reset — and after the faces finish loading. Whenever a
  // cue no longer fits the design in force, ONE `fit` entry of cueSplits is
  // appended (visible in History, undoable) and a notice says so. The size is
  // never touched. Idempotent by construction: after the append every cue
  // fits, so the next run finds nothing. (2026-09-17 10:06 — the first cut
  // ran only inside `commit`, so Undoing the chat's shrink restored 165 px
  // with no split: "how is that make it fit? where is it?")
  //
  // One exception: Undo of a fit entry itself means "not this split" — the
  // gate stands down for that one change instead of re-adding it.
  const skipFitOnceRef = useRef(false);
  const [fontsEpoch, setFontsEpoch] = useState(0);
  useEffect(() => {
    if (typeof document === "undefined" || !document.fonts) return;
    const bump = (): void => setFontsEpoch((n) => n + 1);
    document.fonts.ready.then(bump).catch(() => {});
    document.fonts.addEventListener("loadingdone", bump);
    return () => document.fonts.removeEventListener("loadingdone", bump);
  }, []);
  useEffect(() => {
    if (!inputs?.measuredFit || !serverReadyRef.current || draft.clip !== jobId) return;
    if (approvePhase === "rendering") return; // the approved journal is being read from disk
    if (skipFitOnceRef.current) {
      skipFitOnceRef.current = false;
      return;
    }
    // Never judge a fit while a face is still loading: the canvas would
    // measure a fallback face and journal a split the real face doesn't need
    // (seen on the first cut — two fit entries on one load). `loadingdone`
    // bumps fontsEpoch and re-runs this once the faces are in.
    if (typeof document !== "undefined" && document.fonts?.status === "loading") return;
    const effectiveNow = applyEditState(inputs.inputProps, draft.state);
    const measFor = browserMeasureFor(effectiveNow);
    if (!measFor) return; // face not loaded yet — fontsEpoch re-runs this when it is
    const fit = fitEntryForDesignCommit({ props: effectiveNow, measFor, now: Date.now() });
    if (!fit) return;
    setDraft((d) => (d.clip === jobId ? { clip: d.clip, state: appendEntry(d.state, fit) } : d));
    setFitNotice(`${fit.label} — too wide for the frame at this size. Undo removes the split.`);
  }, [draft, inputs, jobId, approvePhase, fontsEpoch]);

  // Committed history (baked cue edits / promoted design) is not undoable —
  // its effects live in arabic.srt / the channel config, not the journal.
  const lastEntry = editState.journal[editState.journal.length - 1];
  const lastCommitted = !!lastEntry && (lastEntry.bakedAt !== undefined || lastEntry.promotedAt !== undefined);
  const canUndo = editState.journal.length > 0 && !lastCommitted;

  const undo = useCallback((): void => {
    setLivePatch(null); // never let a stale preview mask the replayed state
    setDraft((d) => {
      const last = d.state.journal[d.state.journal.length - 1];
      if (last && (last.bakedAt !== undefined || last.promotedAt !== undefined)) return d;
      if (last?.source === "fit") skipFitOnceRef.current = true; // "not this split"
      return { clip: d.clip, state: undoLast(d.state) };
    });
  }, []);

  const reset = useCallback((): void => {
    setLivePatch(null);
    setDraft((d) => ({ clip: d.clip, state: emptyEditState() }));
    clearDraft(jobId);
    setResetArmed(false);
  }, [jobId]);

  // History "Undo to here": pop every entry AFTER `index`, stopping if the tail
  // is committed history (baked/promoted) — those effects live outside the
  // journal and can't be replayed away.
  const undoTo = useCallback((index: number): void => {
    setLivePatch(null);
    setDraft((d) => {
      let s = d.state;
      while (s.journal.length > index + 1) {
        const last = s.journal[s.journal.length - 1];
        if (last && (last.bakedAt !== undefined || last.promotedAt !== undefined)) break;
        if (last?.source === "fit") skipFitOnceRef.current = true; // "not this split"
        s = undoLast(s);
      }
      return { clip: d.clip, state: s };
    });
  }, []);

  // ⌘Z/Ctrl+Z is NOT bound here: `FocusPanel` owns it (capture phase, text
  // fields exempt) and calls the same `undo`. Two window listeners for one
  // keystroke would pop two journal entries.

  // Two-step Reset disarms itself.
  useEffect(() => {
    if (!resetArmed) return;
    const t = window.setTimeout(() => setResetArmed(false), 2500);
    return () => window.clearTimeout(t);
  }, [resetArmed]);

  // ---- Test render: start → poll → play the slice in a modal ----
  const runTestRender = useCallback((aroundCue: number | null): void => {
    setTestUi({ id: null, status: "starting" });
    startTestRender(jobId, { aroundCue, editState })
      .then(({ id }) => setTestUi({ id, status: "running" }))
      .catch((err: unknown) =>
        setTestUi({ id: null, status: "failed", error: err instanceof Error ? err.message : String(err) }),
      );
  }, [jobId, editState]);

  useEffect(() => {
    if (!testUi || testUi.status !== "running" || !testUi.id) return;
    const id = testUi.id;
    const iv = window.setInterval(() => {
      getTestRenderStatus(jobId, id)
        .then((s) => {
          if (s.status === "done") setTestUi({ id, status: "done", window: s.window });
          else if (s.status === "failed") setTestUi({ id, status: "failed", error: s.error });
        })
        .catch(() => { /* transient — keep polling */ });
    }, 1500);
    return () => window.clearInterval(iv);
  }, [jobId, testUi]);

  /**
   * Follow a job this Studio started — Approve & render, or any Edit-bar
   * action (re-cut, trim, polish, reframe, remix, restore). One poll loop
   * (below) serves them all: the header shows Rendering…, autosave pauses,
   * and when the job lands the preview refetches and the notice says so.
   * Restore lands at once (the job is already ready) — same path, no wait.
   */
  const followJob = useCallback((doneText?: string, barGroup?: EditGroupId): void => {
    setApproveUi({ phase: "rendering", ...(doneText ? { doneText } : {}), ...(barGroup ? { barGroup } : {}) });
    setDoneNotice(null);
  }, []);

  // ---- Approve & render: bake + re-render, then follow the job ----
  const runApprove = useCallback((alsoPromote: boolean): void => {
    followJob();
    void (async () => {
      try {
        let state = editState;
        if (alsoPromote) {
          const promoted = await promoteDesign(jobId, state);
          state = promoted.editState;
          setDraft({ clip: jobId, state });
          lastSavedRef.current = JSON.stringify(state);
        }
        const res = await approveRender(jobId, state);
        // The server's post-bake journal ({bakedAt} markers) replaces ours.
        setDraft({ clip: jobId, state: res.editState });
        lastSavedRef.current = JSON.stringify(res.editState);
        setSync("saved");
      } catch (err) {
        setApproveUi({ phase: "failed", error: err instanceof Error ? err.message : String(err) });
      }
    })();
  }, [jobId, editState, followJob]);

  useEffect(() => {
    if (approveUi?.phase !== "rendering") return;
    const doneText =
      approveUi.doneText
      ?? "New render is live — it's in the Library now. The previous version stays restorable under Edit this clip → Versions.";
    const barGroup = approveUi.barGroup;
    let refreshing = false;
    const iv = window.setInterval(() => {
      if (refreshing) return;
      getJob(jobId)
        .then(async (j) => {
          if (j.status === "ready") {
            refreshing = true;
            window.clearInterval(iv);
            try {
              // SRT changed (baked cues, or a re-cut rewrote it) → refetch
              // inputs; reconcile keeps any edits the operator stacked while
              // the render ran. The edit state is best-effort on its own: a
              // re-cut may have wiped it, and the preview must still refresh.
              const inp = await getStudioInputs(jobId);
              setInputs(inp);
              const es = await getEditState(jobId).catch(() => null);
              if (es) {
                let state = es.editState;
                if (barGroup === "versions") {
                  // Restore brings back a different caption file. The server
                  // drops cue-bound entries for the jobs editJob.ts owns; the
                  // restore route lives elsewhere, so the Studio does the same
                  // drop here and writes it back through the same PUT.
                  const kept = state.journal.filter((e) => e.patch.cues === undefined && e.patch.cueSplits === undefined);
                  if (kept.length !== state.journal.length) {
                    state = { ...state, journal: kept };
                    await putEditState(jobId, state).catch(() => { /* autosave retries */ });
                  }
                }
                // The server's fresh read REPLACES the local journal — no
                // localWins resurrection: after a caption-changing job the
                // local cue-text entries point at cues that no longer exist,
                // and the crash buffer would only bring them back (item 1b).
                clearDraft(jobId);
                setDraft({ clip: jobId, state });
                lastSavedRef.current = JSON.stringify(state);
              }
            } catch { /* preview refresh is best-effort; the render is done */ }
            setJobView(toView(j));
            setJobEpoch((e) => e + 1);
            setApproveUi(null);
            setDoneNotice(doneText);
          } else if (j.status === "failed" || j.status === "incomplete") {
            window.clearInterval(iv);
            setJobView(toView(j));
            setJobEpoch((e) => e + 1);
            setApproveUi({
              phase: "failed",
              error: j.errorMsg ?? "render failed — see the log under Details on the video page",
              ...(barGroup ? { barGroup } : {}),
            });
          }
        })
        .catch(() => { /* transient — keep polling */ });
    }, 2500);
    return () => window.clearInterval(iv);
  }, [jobId, approveUi?.phase, approveUi?.doneText, approveUi?.barGroup]);

  // ---- Save design to channel (explicit action) ----
  const runPromote = useCallback((): void => {
    setPromoting(true);
    setPromoteNotice(null);
    promoteDesign(jobId, editState)
      .then((res) => {
        setDraft({ clip: jobId, state: res.editState });
        lastSavedRef.current = JSON.stringify(res.editState);
        setSync("saved");
        setPromoteNotice(
          `Saved to “${res.channel}” (${res.promotedFields.length} field${res.promotedFields.length === 1 ? "" : "s"}) — new videos on this channel render with it.`,
        );
      })
      .catch((err: unknown) => {
        setPromoteNotice(`Couldn't save to channel: ${err instanceof Error ? err.message : String(err)}`);
      })
      .finally(() => setPromoting(false));
  }, [jobId, editState]);

  // ---- Effective props: journal (+ live gesture) replayed over the
  //      render pipeline's own inputProps. This IS what the Player mounts —
  //      the numbers in the inspector are the numbers the composition gets.
  const effective = useMemo(
    () => (inputs ? applyEditState(inputs.inputProps, editState, livePatch) : null),
    [inputs, editState, livePatch],
  );
  const layoutView = useMemo(() => (effective ? baselineLayout(effective) : null), [effective]);
  const brandView = useMemo(() => (effective ? baselineBrand(effective) : null), [effective]);

  // A cue-count SHRINK (undo a "split it in two", or a re-fetch after an
  // approve-render) can leave the selection pointing past the end — the chat
  // chip would then quote an empty string (`Caption 7 · ""`) and the panel
  // would show a cue editor for a cue that no longer exists. Drop the index,
  // keep the element: the operator is still talking about the captions.
  const cueCount = effective?.cues.length ?? null;
  useEffect(() => {
    if (cueCount == null) return;
    setSelection((s) => (s.cueIdx != null && s.cueIdx >= cueCount ? { ...s, cueIdx: null } : s));
  }, [cueCount]);

  // ---- Styling v2: font universe, channel font upload, custom presets ----
  const fontFamilies = useMemo(
    () => (inputs ? [...new Set((inputs.inputProps.fontFaces ?? []).map((f) => f.family))] : []),
    [inputs],
  );
  // The clip's PRE-JOURNAL design — what panel Reset restores.
  const baselineView = useMemo(
    () =>
      inputs
        ? {
            ...baselineLayout(inputs.inputProps),
            brand: baselineBrand(inputs.inputProps),
          }
        : null,
    [inputs],
  );
  const [customPresets, setCustomPresets] = useState<CustomStylePreset[]>([]);
  useEffect(() => {
    let live = true;
    listStylePresets()
      .then((p) => { if (live) setCustomPresets(p); })
      .catch(() => { /* presets are additive — a fetch failure hides them, nothing breaks */ });
    return () => { live = false; };
  }, []);
  // Presets referencing a family this clip can't resolve are hidden from
  // APPLY (stale channel font) — never silently font-fallback a design.
  const applicablePresets = useMemo(
    () =>
      customPresets.filter((p) => {
        const b = p.patch.brand;
        return (["captionFont", "punchFont", "speakerFont"] as const).every(
          (role) => !b?.[role] || fontFamilies.includes(b[role]!),
        );
      }),
    [customPresets, fontFamilies],
  );
  const handleUploadFont = useCallback(
    async (family: string, dataUrl: string): Promise<FontAddResult> => {
      if (!inputs?.channelSlug) return { error: "This clip has no channel — fonts upload from onboarding." };
      try {
        // The server may answer an upload with adopted / existing / library
        // instead of uploaded (a family some brand already has — spec
        // 2026-09-16 round 2 §2.5); the picker words its notice by status.
        const r = await uploadChannelFont(inputs.channelSlug, family, dataUrl);
        // Re-fetch inputs so the new face (and its @font-face) reaches the
        // preview + picker immediately.
        const inp = await getStudioInputs(jobId);
        setInputs(inp);
        return { error: null, status: r.status };
      } catch (err) {
        return { error: err instanceof Error ? err.message : String(err) };
      }
    },
    [inputs?.channelSlug, jobId],
  );
  /** Adopt another brand's family onto this clip's channel (no bytes travel)
   *  — then the SAME inputs re-fetch an upload does, so the adopted faces
   *  reach the preview + picker immediately. Spec 2026-09-16 round 2 §2.5. */
  const handleAdoptFont = useCallback(
    async (family: string): Promise<FontAddResult> => {
      if (!inputs?.channelSlug) return { error: "This clip has no channel — fonts are adopted from onboarding." };
      try {
        const r = await adoptChannelFont(inputs.channelSlug, family);
        const inp = await getStudioInputs(jobId);
        setInputs(inp);
        return { error: null, status: r.status };
      } catch (err) {
        return { error: err instanceof Error ? err.message : String(err) };
      }
    },
    [inputs?.channelSlug, jobId],
  );
  const handleSavePreset = useCallback(
    async (label: string): Promise<string | null> => {
      if (!layoutView || !brandView) return "Design still loading.";
      try {
        // Full explicit restatement of the current look — portable.
        const preset = await saveStylePreset(label, {
          layout: { caption: { ...layoutView.caption } },
          brand: {
            captionColor: brandView.captionColor,
            captionAccent: brandView.captionAccent,
            captionFont: brandView.captionFont,
            punchFont: brandView.punchFont,
            speakerFont: brandView.speakerFont,
          },
        });
        setCustomPresets((prev) => [...prev, preset]);
        return null;
      } catch (err) {
        return err instanceof Error ? err.message : String(err);
      }
    },
    [layoutView, brandView],
  );
  const handleDeletePreset = useCallback(async (id: string): Promise<void> => {
    try {
      await deleteStylePreset(id);
      setCustomPresets((prev) => prev.filter((p) => p.id !== id));
    } catch {
      // Failed delete: re-fetch so the UI reflects the server's truth
      // instead of silently keeping (or dropping) a stale row.
      try {
        setCustomPresets(await listStylePresets());
      } catch { /* offline — leave as-is */ }
    }
  }, []);

  // The Edit bar's Start & end group scrubs THIS Player (a Remotion Player,
  // not a <video>) through TrimExtendPanel's seek adapter. Seconds are the
  // mp4 timeline's — the composition starts with the baked cover, exactly
  // like the file does, so no cover offset is added here.
  const seekFps = inputs?.fps ?? 30;
  const seekFrames = inputs?.durationInFrames ?? 0;
  const previewSeek = useMemo<PreviewSeek>(() => ({
    currentSec: () => (playerRef.current?.getCurrentFrame() ?? 0) / seekFps,
    seekTo: (sec) => playerRef.current?.seekTo(Math.max(0, Math.round(sec * seekFps))),
    play: () => playerRef.current?.play(),
    durationSec: () => (seekFrames > 0 ? seekFrames / seekFps : undefined),
  }), [seekFps, seekFrames]);

  const rendering = approveUi?.phase === "rendering";
  editLockedRef.current = editLocked(jobView, rendering);
  const hasDesign = editState.journal.some((e) => touchesLayoutOrBrand(e.patch));
  const designPromoted = hasDesign
    && editState.journal.every((e) => !touchesLayoutOrBrand(e.patch) || e.promotedAt !== undefined);
  const pendingChannel = hasPendingChannelEntries(editState);
  const canPromote = hasDesign && !designPromoted && !rendering;

  // The teach-once coach card and the first-run Tweak pill are retired
  // (spec §4): the Focus panel's empty state and the History panel are the
  // permanent homes for "Save as my style", and the panel itself is the
  // standing invitation the pill used to be. `canPromote` / `runPromote`
  // stay — both of those surfaces call them.

  if (error) {
    return (
      <div>
        <StudioHeader
          title={jobView ? displayTitle(jobView) : null} onBack={onBack} edits={0} canUndo={false} onUndo={undo}
          resetArmed={false} onReset={() => {}} sync="error"
          onTestRender={() => {}} onApprove={() => {}} actionsDisabled rendering={false}
        />
        <div
          style={{
            background: "rgba(248,113,113,.06)",
            border: "1px solid rgba(248,113,113,.30)",
            borderRadius: 14,
            padding: 16,
            color: "var(--ql-danger)",
            fontSize: 13,
            fontFamily: "var(--ql-font-mono)",
          }}
        >
          Couldn&apos;t load Studio inputs: {error}
        </div>
      </div>
    );
  }

  if (!inputs || !effective || !layoutView || !brandView) {
    return (
      <div>
        <StudioHeader
          title={jobView ? displayTitle(jobView) : null} onBack={onBack} edits={0} canUndo={false} onUndo={undo}
          resetArmed={false} onReset={() => {}} sync="loading"
          onTestRender={() => {}} onApprove={() => {}} actionsDisabled rendering={false}
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            color: "var(--ql-fg-muted)",
            fontSize: 13,
            padding: 40,
          }}
        >
          <Loader2 size={16} className="ql-spin" />
          Loading clip inputs…
        </div>
      </div>
    );
  }

  const { fps, coverHoldSec, durationInFrames } = inputs;
  const coverFrames = Math.round(coverHoldSec * fps);
  // Open ON a caption, never the pre-cue gap at frame 0 — cue 1 typically
  // starts 50-500ms in, so frame 0 renders captionless and the studio's
  // first impression reads as "captions are gone" (tour-caught).
  const initialFrame = effective.cues.length > 0
    ? Math.min(
        coverFrames + Math.round((effective.cues[0]!.startMs / 1000) * fps) + 1,
        Math.max(0, durationInFrames - 1),
      )
    : 0;
  // Cue times are CONTENT ms; the player timeline starts with the baked
  // cover (when on), so every seek adds the cover offset.
  const contentMs = Math.max(1, ((frame - coverFrames) / fps) * 1000);
  const activeCueIdx = effective.cues.findLastIndex(
    (c) => contentMs >= c.startMs && contentMs < c.endMs,
  );
  /**
   * The cue the TRANSPORT names. `activeCueIdx` goes to -1 in every gap
   * between cues, and a line that vanishes and comes back reflows ▶ and the
   * clock on every silence. So: the last cue that has STARTED (it simply
   * holds through the gap), and before the first one has started, cue 1 —
   * the line is a way into the captions, not a playback readout, and a
   * stable row beats a truthful blank one.
   */
  const startedCueIdx = effective.cues.findLastIndex((c) => contentMs >= c.startMs);
  const transportCueIdx = startedCueIdx >= 0 ? startedCueIdx : (effective.cues.length > 0 ? 0 : -1);
  const totalCueMs = effective.cues.length
    ? Math.max(...effective.cues.map((c) => c.endMs))
    : 1;
  const rtl = (brandView.direction ?? "rtl") === "rtl";
  const selectedCue =
    selection.cueIdx != null && selection.cueIdx >= 0 && selection.cueIdx < effective.cues.length
      ? effective.cues[selection.cueIdx]!
      : null;

  const seekToCue = (startMs: number): void => {
    playerRef.current?.seekTo(coverFrames + Math.round((startMs / 1000) * fps));
  };

  // Chat Undo only maps 1:1 onto undoLast() while the journal's last
  // entry IS the chat patch (matched by its label = the explanation) and
  // isn't committed history — a manual edit, an undo, an approve or a
  // promotion in between hides the affordance.
  const undoableChatLabel = lastEntry?.source === "chat" && !lastCommitted ? lastEntry.label : null;

  // Test render centers on the selected cue, else the cue under the
  // playhead, else the first cue.
  const testCueIdx = selection.cueIdx ?? (activeCueIdx >= 0 ? activeCueIdx : null);

  /** Select a cue AND put the playhead on it — the one gesture behind the
   *  transcript list, the cue line under the preview, the timeline strip and
   *  the chat's "Fix a caption" chip. */
  const pickCue = (i: number): void => {
    const cue = effective.cues[i];
    if (!cue) return;
    seekToCue(cue.startMs);
    setSelection({ el: "caption", cueIdx: i });
  };


  // The dock owns the thread + model + balance chrome; these are the parts
  // only the editor can supply (live journal, selection, commit path).
  const dockPanel: DockPanelProps = {
    editState,
    selection,
    onApplyPatch: (patch, explanation, channelPending) =>
      commit(patch, explanation, "chat", channelPending ? "channel-pending" : undefined),
    undoableChatLabel,
    onUndo: undo,
    onOpenClipTools: openClipTools,
    // The selection was ALREADY serialized into every turn; these make it
    // visible and actionable in the composer (spec §4). `cueLabel` first:
    // the chip QUOTES this, and raw `<em>` markup in a quote reads as a bug
    // (the same leak the timeline chips guard against).
    selectedCueText: selectedCue ? cueLabel(selectedCue.text) : null,
    onClearSelection: () => setSelection(NO_SELECTION),
    // Same cue the transport line names — "Fix a caption" and clicking
    // that line must land on the SAME caption, or the chip lies.
    onFixCaption: () => pickCue(transportCueIdx),
    hasCues: effective.cues.length > 0,
  };

  // Mobile keeps the drawer no matter what the stored mode says — so the
  // header toggle must reflect the EFFECTIVE placement, and disappear where
  // "dock left" isn't a thing.
  const dockLeft = !isMobile && dockMode === "left";

  const chatDock = (
    <StudioChatDock
      clipName={inputs.clipName}
      panel={dockPanel}
      mode={dockLeft ? "left" : "drawer"}
      onModeChange={changeDockMode}
      canChangeMode={!isMobile}
      onOpenSettings={onOpenSettings}
      onAssistantTurn={() => { if (collapsedRef.current) setChatUnread(true); }}
      height="100%"
    />
  );

  // ---- The stage's three pieces: preview, transport, panel ----

  /** The player box + its canvas overlay at an exact height (the stage hands
   *  a number; mobile hands a CSS length). The overlay MUST share this box —
   *  it maps pointer deltas through width/CANVAS_WIDTH. */
  const previewBox = (height: number | string): React.JSX.Element => (
    <div
      style={{
        position: "relative",
        aspectRatio: "9 / 16",
        height,
        borderRadius: 16,
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,.08)",
        background: "#000",
        boxShadow: "0 12px 40px rgba(0,0,0,.45)",
      }}
    >
      <Player
        ref={playerRef}
        component={ClipComposition}
        inputProps={effective}
        durationInFrames={durationInFrames}
        fps={fps}
        compositionWidth={inputs.width}
        compositionHeight={inputs.height}
        style={{ width: "100%", height: "100%" }}
        clickToPlay={false}
        initialFrame={initialFrame}
        acknowledgeRemotionLicense
      />
      <StudioCanvasOverlay
        effective={effective}
        selection={selection}
        onSelect={setSelection}
        onLive={setLivePatch}
        onCommit={(patch, label) => commit(patch, label, "canvas")}
        activeCueIdx={activeCueIdx}
      />
    </div>
  );

  const transportCue = transportCueIdx >= 0 ? effective.cues[transportCueIdx] ?? null : null;
  const transportCueSelected = selection.el === "caption" && selection.cueIdx === transportCueIdx;

  /** ▶ + time + THE CURRENT CUE as one clickable line (spec §3): the shortest
   *  path from "caption 3 is wrong" to caption 3 being selected. */
  const transport = (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, minWidth: 0 }}>
      <button
        type="button"
        onClick={() => playerRef.current?.toggle()}
        style={{ ...iconBtnStyle, width: 44, height: 44, flexShrink: 0 }}
        title={playing ? "Pause" : "Play"}
      >
        {playing ? <Pause size={18} strokeWidth={2} /> : <Play size={18} strokeWidth={2} />}
      </button>
      <span
        style={{
          fontFamily: "var(--ql-font-mono)",
          fontSize: 13,
          color: "var(--ql-fg-muted)",
          flexShrink: 0,
        }}
      >
        {formatTime(frame / fps)} / {formatTime(durationInFrames / fps)}
      </span>
      {transportCue && (
        <button
          type="button"
          onClick={() => pickCue(transportCueIdx)}
          dir={rtl ? "rtl" : "ltr"}
          title="Edit this caption"
          style={{
            // Capped so a long cue can never make the stage column wider than
            // the preview it is centred under.
            maxWidth: 240,
            minWidth: 0,
            padding: "7px 11px",
            borderRadius: 10,
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: 12.5,
            lineHeight: 1.4,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            border: "1px solid " + (transportCueSelected ? "rgba(255,213,0,.85)" : "rgba(255,255,255,.08)"),
            background: transportCueSelected ? "rgba(255,213,0,.12)" : "rgba(255,255,255,.03)",
            color: transportCueSelected ? "var(--ql-yellow)" : "var(--ql-fg)",
          }}
        >
          {cueLabel(transportCue.text)}
        </button>
      )}
    </div>
  );

  /** EXACTLY the object the right-hand inspector column used to be mounted
   *  with — the Focus panel's "More options" mounts it verbatim, so nothing
   *  is lost and nothing is forked (spec §3). */
  const inspectorProps: StudioInspectorProps = {
    selection,
    onSelect: setSelection,
    // baselineLayout materializes only the geometry it can default;
    // `overlays` has no baseline (absent = none), so it rides along from the
    // effective props. Passing it is what makes the Overlays editor
    // non-destructive — it writes the WHOLE list, so it has to SEE the whole
    // list, or one Add would silently drop the channel's CTA.
    layout: { ...layoutView, overlays: effective.layout?.overlays ?? [] },
    brand: brandView,
    // Name-size / role-line edits preview instantly only when the card is
    // drawn LIVE from text; an older clip's card is a baked PNG and the panel
    // says so instead of implying a live preview.
    speakerCardLive: effective.speakers.some((s) => s.live != null),
    cue: selectedCue,
    clipName: inputs.clipName,
    durationSec: effective.durationSec,
    cueCount: effective.cues.length,
    journal: editState.journal,
    onPreview: setLivePatch,
    onCommit: (patch, label) => commit(patch, label, "inspector"),
    channelSlug: inputs.channelSlug,
    fontFamilies,
    onUploadFont: inputs.channelSlug ? handleUploadFont : null,
    onAdoptFont: inputs.channelSlug ? handleAdoptFont : undefined,
    customPresets: applicablePresets,
    onSavePreset: handleSavePreset,
    onDeletePreset: handleDeletePreset,
    baseline: baselineView!,
  };

  const focusPanelProps: FocusPanelProps = {
    host: "studio",
    inspector: inspectorProps,
    selection,
    onSelect: setSelection,
    cues: effective.cues,
    onSelectCue: pickCue,
    // The panel's footer names what ⌘Z would pop; committed history (baked /
    // promoted) is not undoable, which is exactly what `canUndo` encodes.
    undoLabel: canUndo ? lastEntry?.label ?? null : null,
    onUndo: undo,
    onSavePromote: runPromote,
    canPromote,
    promoting,
  };

  return (
    // Capped + centered so a wide monitor doesn't sprawl the player and
    // inspector to opposite edges; bottom padding keeps the fixed Design-agent
    // pill from covering the last rows (presets / History) at full scroll.
    <div style={{ maxWidth: 1480, margin: "0 auto", paddingBottom: 76 }}>
      {/* Everything above the stage lives in ONE measured block — the stage
          takes the viewport that is left under it (see `stageH`). `flow-root`
          is load-bearing: without it the header's bottom margin collapses
          THROUGH this wrapper, and the measured bottom lands 20 px above
          where the stage actually starts. */}
      <div ref={headerRef} style={{ display: "flow-root" }}>
        <StudioHeader
          title={jobView ? displayTitle(jobView) : null}
          onBack={onBack}
          edits={editState.journal.length}
          canUndo={canUndo}
          onUndo={undo}
          resetArmed={resetArmed}
          onReset={() => (resetArmed ? reset() : setResetArmed(true))}
          sync={sync}
          onTestRender={() => runTestRender(testCueIdx)}
          onApprove={() => setApproveUi({ phase: "confirm" })}
          actionsDisabled={rendering || (testUi != null && (testUi.status === "starting" || testUi.status === "running"))}
          rendering={rendering}
        />

        {doneNotice && (
          <NoticeRow tone="good" text={doneNotice} onDismiss={() => setDoneNotice(null)} />
        )}

        {/* "Save as my style" is fired from INSIDE the stage (the panel's
            empty-state row) and from the History panel below it — but its
            outcome has to be readable without scrolling, so it lands here,
            above the fold and inside the measured region. A failure that
            paints under a viewport-filling stage is a failure nobody reads. */}
        {promoteNotice && (
          <NoticeRow
            tone={promoteNotice.startsWith("Couldn't") ? "bad" : "good"}
            text={promoteNotice}
            onDismiss={() => setPromoteNotice(null)}
          />
        )}
        {fitNotice && <NoticeRow tone="good" text={fitNotice} onDismiss={() => setFitNotice(null)} />}

        {/* Edit this clip — captions, start & end, sound, camera, a fresh
            cut, versions (spec 2026-09-16 §2.4). Inside the measured chrome
            on purpose: opening a group re-fits the stage under it instead of
            pushing the preview off the bottom of the viewport, and the group
            body caps its own height so the stage is never crushed. */}
        <EditClipBar
          view={jobView}
          open={editBarOpen}
          onToggle={() => {
            // Collapsing the bar closes its drawer too.
            setEditBarOpen((o) => { if (o) setEditGroup(null); return !o; });
          }}
          group={editGroup}
          onGroup={setEditGroup}
          rendering={rendering}
          barRef={editBarRef}
        />
      </div>

      <div
        style={{
          display: "grid",
          // TWO columns now: the chat (or its 48 px rail) and the Focus
          // editor stage. The old right-hand inspector column is gone — its
          // whole surface lives one click away inside the stage panel
          // ("More options"), scrolling INSIDE the panel (spec §4).
          gridTemplateColumns: isMobile
            ? "1fr"
            : dockLeft
              ? `${chatRail ? 48 : 360}px minmax(0, 1fr)`
              : "minmax(0, 1fr)",
          gap: 18,
          alignItems: "start",
          marginBottom: 16,
        }}
      >
        {/* ---- Chat dock column (left mode only) ---- */}
        {dockLeft && (
          <div
            style={{
              minWidth: 0,
              position: "sticky",
              top: 12,
              alignSelf: "start",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              // Exactly the stage's height: the chat and the video are one
              // band, and neither is the reason the page scrolls.
              height: chatRail ? "auto" : stageH,
            }}
          >
            {chatRail ? (
              <button
                type="button"
                onClick={() => setCollapsed(false)}
                title="Open the design agent"
                aria-label="Open the design agent"
                style={{
                  position: "relative",
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  border: "1px solid rgba(255,213,0,.35)",
                  background: "rgba(255,213,0,.08)",
                  color: "var(--ql-yellow)",
                  display: "grid",
                  placeItems: "center",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                <Sparkles size={16} />
                {chatUnread && (
                  <span
                    aria-label="New reply"
                    style={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      width: 8,
                      height: 8,
                      borderRadius: 999,
                      background: "var(--ql-danger)",
                    }}
                  />
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setCollapsed(true)}
                title="Collapse the chat to a rail"
                style={{
                  alignSelf: "flex-end",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 8px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,.08)",
                  background: "rgba(255,255,255,.03)",
                  color: "var(--ql-fg-subtle)",
                  fontFamily: "inherit",
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: ".04em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                }}
              >
                <PanelLeftClose size={11} />
                Collapse
              </button>
            )}
            {/* Kept MOUNTED while collapsed (display:none, not unmounted) so
                a turn in flight still lands — that reply is what raises the
                rail's unread dot. */}
            <div style={{ flex: 1, minHeight: 0, display: chatRail ? "none" : "block" }}>
              {chatDock}
            </div>
          </div>
        )}

        {/* ---- The stage: [FocusPanel | preview + transport] ----
            Desktop hands the whole band to FocusEditor, which sizes the
            preview to fit. Mobile has no room for a side-by-side, so the
            panel stacks BELOW the preview (spec §4). */}
        {/* The Edit bar's open group is a DRAWER over the top of the stage:
            it takes no layout space, so the preview keeps its size and the
            page never scrolls for it (round 1, item 3). */}
        <div style={{ position: "relative", minWidth: 0 }}>
          {editBarOpen && editGroup && (
            <EditClipDrawer
              jobId={jobId}
              view={jobView}
              group={editGroup}
              onClose={() => setEditGroup(null)}
              seek={previewSeek}
              onJobStarted={(g) => followJob(EDIT_GROUP_DONE[g], g)}
              rendering={rendering}
              epoch={jobEpoch}
            />
          )}
          {isMobile ? (
            <div style={{ minWidth: 0, display: "grid", gap: 12, justifyItems: "center" }}>
              {previewBox("min(64vh, 620px)")}
              {transport}
              <div style={{ width: "100%", height: "min(70vh, 560px)" }}>
                <FocusPanel {...focusPanelProps} width="100%" />
              </div>
            </div>
          ) : (
            <FocusEditor
              panel={focusPanelProps}
              stageHeight={stageH}
              render={(h) => previewBox(h)}
              transport={transport}
            />
          )}
        </div>
      </div>

      {/* Cue timeline: one block per cue; click → seek + select the cue. */}
      <div style={{ fontSize: 11, color: "var(--ql-fg-muted)", marginBottom: 8, letterSpacing: ".04em", textTransform: "uppercase" }}>
        Cue timeline · {effective.cues.length} cues — click a cue to edit its text
      </div>
      <div
        style={{
          display: "flex",
          gap: 4,
          overflowX: "auto",
          paddingBottom: 10,
        }}
      >
        {effective.cues.map((c, i) => {
          const active = i === activeCueIdx;
          const isSelected = selection.el === "caption" && selection.cueIdx === i;
          const pct = ((c.endMs - c.startMs) / totalCueMs) * 100;
          return (
            <button
              key={`${c.startMs}-${i}`}
              type="button"
              onClick={() => {
                seekToCue(c.startMs);
                setSelection({ el: "caption", cueIdx: i });
              }}
              title={`${formatTime(c.startMs / 1000)} → ${formatTime(c.endMs / 1000)}`}
              dir={rtl ? "rtl" : "ltr"}
              style={{
                flex: `0 0 max(${pct}%, 92px)`,
                minWidth: 92,
                padding: "8px 10px",
                borderRadius: 10,
                cursor: "pointer",
                textAlign: "center",
                fontFamily: "inherit",
                fontSize: 12,
                lineHeight: 1.5,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                border: "1px solid " + (isSelected ? "rgba(255,213,0,.85)" : active ? "rgba(255,213,0,.5)" : "rgba(255,255,255,.08)"),
                boxShadow: isSelected ? "0 0 0 1px rgba(255,213,0,.4)" : undefined,
                background: active || isSelected ? "rgba(255,213,0,.12)" : "rgba(255,255,255,.03)",
                color: active || isSelected ? "var(--ql-yellow)" : "var(--ql-fg)",
              }}
            >
              <span style={{ display: "block", fontFamily: "var(--ql-font-mono)", fontSize: 10, color: active || isSelected ? "var(--ql-yellow)" : "var(--ql-fg-subtle)", marginBottom: 2 }}>
                {i + 1} · {formatTime(c.startMs / 1000)}
              </span>
              {cueLabel(c.text)}
            </button>
          );
        })}
      </div>

      {/* ---- History & preferences: every applied tweak + the persistent
          "Save as my style" home ---- */}
      <StudioHistoryPanel
        journal={editState.journal}
        onUndoTo={undoTo}
        channelSlug={inputs.channelSlug}
        canPromote={canPromote}
        promoting={promoting}
        designPromoted={designPromoted}
        onPromote={runPromote}
        promoteNotice={promoteNotice}
      />

      {/* ---- Approve dialog / failure ---- */}
      {approveUi && approveUi.phase !== "rendering" && (
        <StudioModal
          onClose={() => setApproveUi(null)}
          title={approveUi.phase === "failed" ? (approveUi.barGroup ? "That didn't finish" : "Render failed") : "Approve & render"}
        >
          {approveUi.phase === "failed" ? (
            <>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13.5, lineHeight: 1.6, color: "var(--ql-fg-muted)", marginBottom: 20 }}>
                <AlertTriangle size={16} style={{ color: "var(--ql-danger)", flexShrink: 0, marginTop: 2 }} />
                <span>{approveUi.error}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" style={ctaGhostStyle} onClick={() => setApproveUi(null)}>Close</button>
                {/* Approve's "Try again" bakes + re-renders the DESIGN — the
                    wrong retry for a bar action; those re-run from the bar. */}
                {!approveUi.barGroup && (
                  <button type="button" style={ctaSmallStyle} onClick={() => setApproveUi({ phase: "confirm" })}>Try again</button>
                )}
              </div>
            </>
          ) : (
            <ApproveConfirm
              edits={editState.journal.length}
              hasDesign={hasDesign}
              channelSlug={inputs.channelSlug}
              channelName={inputs.channelDisplayName ?? inputs.channelSlug}
              pendingChannel={pendingChannel}
              onCancel={() => setApproveUi(null)}
              onConfirm={runApprove}
            />
          )}
        </StudioModal>
      )}

      {/* ---- Test render modal ---- */}
      {testUi && (
        <StudioModal
          onClose={() => setTestUi(null)}
          title="Test render"
        >
          {testUi.status === "done" && testUi.id ? (
            <>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
                <video
                  src={testRenderFileUrl(jobId, testUi.id)}
                  controls
                  autoPlay
                  loop
                  playsInline
                  style={{
                    height: "min(56vh, 640px)",
                    aspectRatio: "9 / 16",
                    borderRadius: 12,
                    background: "#000",
                    border: "1px solid rgba(255,255,255,.1)",
                  }}
                />
              </div>
              <div style={{ fontSize: 12.5, lineHeight: 1.6, color: "var(--ql-fg-muted)", textAlign: "center" }}>
                This is the REAL pipeline renderer&apos;s output — the same bundle, composition and
                encoder the final export uses
                {testUi.window
                  ? `, sliced ${testUi.window.startSec.toFixed(1)}–${testUi.window.endSec.toFixed(1)}s around cue ${testUi.window.cueIdx + 1}`
                  : ""}.
              </div>
            </>
          ) : testUi.status === "failed" ? (
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13.5, lineHeight: 1.6, color: "var(--ql-fg-muted)" }}>
              <AlertTriangle size={16} style={{ color: "var(--ql-danger)", flexShrink: 0, marginTop: 2 }} />
              <span>{testUi.error ?? "test render failed"}</span>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 13.5, color: "var(--ql-fg-muted)", padding: "18px 4px" }}>
              <Loader2 size={17} className="ql-spin" />
              Rendering a ~6s slice through the real pipeline renderer — usually under a minute…
            </div>
          )}
        </StudioModal>
      )}

      {/* ---- Iterate chat, "drawer" mode: floating toggle + slide-in panel.
          In "left" mode the chat is the first grid column above and neither
          of these renders. ---- */}
      {!dockLeft && (
      <button
        type="button"
        // Explicit, from the VISIBLE state: a bare toggle would flip the stored
        // choice to "open" when "Close chat" is tapped while the tour is
        // holding the chat open (stored false, shown open).
        onClick={() => toggleChat(!chatVisible)}
        title={chatVisible ? "Hide the design agent" : "Chat with the design agent — sizes, positions, colors, wording"}
        style={{
          position: "fixed",
          right: chatVisible ? (isMobile ? 12 : 428) : 20,
          bottom: 20,
          zIndex: 60,
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "11px 16px",
          borderRadius: 999,
          cursor: "pointer",
          fontFamily: "inherit",
          fontSize: 13,
          fontWeight: 700,
          border: "1px solid rgba(255,213,0,.5)",
          background: chatVisible ? "rgba(255,213,0,.16)" : "rgba(20,20,24,.92)",
          color: "var(--ql-yellow)",
          boxShadow: "0 8px 28px rgba(0,0,0,.5)",
          backdropFilter: "blur(6px)",
          transition: "right .18s ease",
        }}
      >
        {chatVisible ? <X size={15} /> : <Sparkles size={15} />}
        {chatVisible ? "Close chat" : "Design agent"}
      </button>
      )}

      {!dockLeft && chatVisible && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            zIndex: 55,
            width: isMobile ? "100vw" : 412,
            maxWidth: "100vw",
            padding: isMobile ? "12px" : "16px",
            // Phones: while the tour's bottom sheet is up the composer moves
            // above it (the sheet publishes its height; 0px otherwise).
            paddingBottom: isMobile ? `calc(84px + ${TOUR_SHEET_PAD})` : 84,
            boxSizing: "border-box",
            background: "rgba(12,12,15,.97)",
            borderLeft: "1px solid rgba(255,255,255,.08)",
            boxShadow: "-16px 0 48px rgba(0,0,0,.5)",
            backdropFilter: "blur(10px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ flex: 1, minHeight: 0 }}>{chatDock}</div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Above-the-fold notices (render done / design promoted or refused)
// ---------------------------------------------------------------------------

/** One dismissible sentence under the header. Lives INSIDE the measured
 *  chrome block, so showing one shortens the stage instead of pushing it off
 *  the bottom of the viewport. */
function NoticeRow({ tone, text, onDismiss }: {
  tone: "good" | "bad";
  text: string;
  onDismiss: () => void;
}): React.JSX.Element {
  const good = tone === "good";
  return (
    <div
      role="status"
      style={{
        display: "flex", alignItems: "center", gap: 10, marginBottom: 16,
        padding: "10px 14px", borderRadius: 12, fontSize: 13,
        border: `1px solid ${good ? "rgba(99,226,183,.4)" : "rgba(248,113,113,.35)"}`,
        background: good ? "rgba(99,226,183,.07)" : "rgba(248,113,113,.07)",
        color: "var(--ql-fg)",
      }}
    >
      {good
        ? <Check size={15} style={{ color: "rgb(99,226,183)", flexShrink: 0 }} />
        : <AlertTriangle size={15} style={{ color: "var(--ql-danger)", flexShrink: 0 }} />}
      <span style={{ minWidth: 0 }}>{text}</span>
      <button
        type="button"
        onClick={onDismiss}
        style={{ ...iconBtnStyle, width: 26, height: 26, marginLeft: "auto", flexShrink: 0 }}
        title="Dismiss"
      >
        <X size={13} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Approve dialog body
// ---------------------------------------------------------------------------

function ApproveConfirm({ edits, hasDesign, channelSlug, channelName, pendingChannel, onCancel, onConfirm }: {
  edits: number;
  hasDesign: boolean;
  channelSlug: string | null;
  channelName: string | null;
  pendingChannel: boolean;
  onCancel: () => void;
  onConfirm: (alsoPromote: boolean) => void;
}): React.JSX.Element {
  // The channel checkbox pre-checks when the chat loop confirmed
  // channel-wide intent ({scope:"channel-pending"} entries).
  const [alsoPromote, setAlsoPromote] = useState(pendingChannel);
  const canPromote = channelSlug !== null && hasDesign;
  return (
    <>
      <div style={{ fontSize: 13.5, lineHeight: 1.65, color: "var(--ql-fg-muted)", marginBottom: 16 }}>
        Renders this clip for real with your {edits} edit{edits === 1 ? "" : "s"} applied — cue text
        edits are written into the captions file, and the design drives the render with the exact
        numbers you see in the inspector. The previous version stays restorable from the clip&apos;s
        history.
      </div>
      {canPromote && (
        <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, lineHeight: 1.55, color: "var(--ql-fg)", marginBottom: 20, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={alsoPromote}
            onChange={(e) => setAlsoPromote(e.target.checked)}
            style={{ marginTop: 3, accentColor: "#FFD500" }}
          />
          <span>
            Also make this the <b>{channelName}</b> brand design — every future video on it renders with it.
            {pendingChannel ? " (You asked for this in chat.)" : ""}
            {" "}
            <a href="#/guide/test-vs-approve" target="_blank" rel="noreferrer" style={{ color: "var(--ql-yellow)" }}>
              What does this do?
            </a>
          </span>
        </label>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button type="button" style={ctaGhostStyle} onClick={onCancel}>Cancel</button>
        <button type="button" style={ctaSmallStyle} onClick={() => onConfirm(canPromote && alsoPromote)}>
          <Check size={14} /> Approve &amp; render
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

const SYNC_LABEL: Record<SyncStatus, { text: string; color: string }> = {
  loading: { text: "loading…", color: "var(--ql-fg-subtle)" },
  saved: { text: "saved", color: "rgba(99,226,183,.9)" },
  saving: { text: "saving…", color: "var(--ql-fg-muted)" },
  unsaved: { text: "unsaved", color: "var(--ql-yellow)" },
  error: { text: "save failed — retrying on next edit", color: "var(--ql-danger)" },
};

function StudioHeader({ title, onBack, edits, canUndo, onUndo, resetArmed, onReset, sync, onTestRender, onApprove, actionsDisabled, rendering }: {
  /** The clip's display title (never its id); null while it loads. */
  title: string | null;
  onBack: () => void;
  edits: number;
  canUndo: boolean;
  onUndo: () => void;
  resetArmed: boolean;
  onReset: () => void;
  sync: SyncStatus;
  onTestRender: () => void;
  onApprove: () => void;
  /** Disables Test render + Approve (mid-render / mid-test / loading). */
  actionsDisabled: boolean;
  /** An approve-render is in flight — Approve shows progress. */
  rendering: boolean;
}): React.JSX.Element {
  const syncChip = SYNC_LABEL[sync];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
      <button onClick={onBack} style={iconBtnStyle} title="Back to clip">
        <ArrowLeft size={16} strokeWidth={2} />
      </button>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 11.5,
            color: "var(--ql-fg-muted)",
            marginBottom: 2,
            maxWidth: 480,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minHeight: 14,
          }}
          title={title ?? undefined}
        >
          {title ?? ""}
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em" }}>
          Studio
          <span style={{ fontWeight: 400, fontSize: 13, color: "var(--ql-fg-muted)", marginLeft: 10 }}>
            live preview — the exact composition the render bakes
          </span>
        </div>
      </div>
      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span
          style={{ fontSize: 11.5, fontFamily: "var(--ql-font-mono)", color: syncChip.color }}
          title="Edits autosave to the server (work/<clip>/edit-state.json)"
        >
          {edits} edit{edits === 1 ? "" : "s"} · {syncChip.text}
        </span>
        <button
          type="button"
          onClick={onUndo}
          disabled={!canUndo}
          style={{ ...ctaGhostStyle, display: "inline-flex", alignItems: "center", gap: 6, opacity: canUndo ? 1 : 0.45, cursor: canUndo ? "pointer" : "default" }}
          title={canUndo ? "Undo last edit (⌘Z)" : edits > 0 ? "Last step is committed history (baked / promoted) — edit again instead" : "Nothing to undo"}
        >
          <Undo2 size={13} /> Undo
        </button>
        <button
          type="button"
          onClick={onReset}
          disabled={edits === 0 && !resetArmed}
          style={{
            ...ctaGhostStyle,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            opacity: edits === 0 && !resetArmed ? 0.45 : 1,
            cursor: edits === 0 && !resetArmed ? "default" : "pointer",
            ...(resetArmed ? { border: "1px solid rgba(248,113,113,.6)", color: "var(--ql-danger)" } : {}),
          }}
          title="Clear every design override (cue edits already baked into the captions stay)"
        >
          <RotateCcw size={13} /> {resetArmed ? "Sure? Click again" : "Reset"}
        </button>
        <button
          type="button"
          onClick={onTestRender}
          disabled={actionsDisabled}
          data-tour="studio-test-render"
          style={{ ...ctaGhostStyle, opacity: actionsDisabled ? 0.45 : 1, cursor: actionsDisabled ? "default" : "pointer" }}
          title="Render a ~6s slice through the REAL pipeline renderer — proof before adopting"
        >
          <FlaskConical size={13} /> Test render
        </button>
        <button
          type="button"
          onClick={onApprove}
          disabled={actionsDisabled}
          data-tour="studio-approve"
          style={{ ...ctaSmallStyle, opacity: actionsDisabled && !rendering ? 0.55 : 1, cursor: actionsDisabled ? "default" : "pointer" }}
          title="Bake cue edits into the captions + re-render this clip with the design applied"
        >
          {rendering ? <Loader2 size={14} className="ql-spin" /> : <Check size={14} />}
          {rendering ? "Rendering…" : "Approve & render"}
        </button>
      </div>
    </div>
  );
}
